import type { PercentFeeRule, RateCard as RateCardData, RateTier } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';

export interface RateCardProps {
  status: 'loading' | 'ready' | 'error';
  card?: RateCardData;
  onRetry: () => void;
}

/**
 * The China Payment rate card (MYP-59): today's exchange rate by tier, and the service fees —
 * both straight from `currencyRate`, nothing computed here.
 *
 * Tiers are labelled in **RM**, which is what the vendor's limits are in (see `RateTier`), and the
 * rate is written "RM 1 = ¥1.591" — a bare 1.591 does not say which way it converts.
 *
 * The fees are the settings the old website shows (3% / RM 10 under ¥500 for top-ups, 5% / RM 20
 * for proxy pay, ¥15 per bank transfer). The live quote (`alipay_rechange`, MYP-60) currently
 * charges differently — China Payment API issues, item 2 — so the footnote says the exact amount
 * comes with the quote.
 */
export function RateCard({ status, card, onRetry }: RateCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="p-0">
      <div className="border-b border-line px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-ink">{t('chinaPayment.rates.title')}</h2>
        <p className="mt-0.5 text-sm text-muted">{t('chinaPayment.rates.subtitle')}</p>
      </div>

      {status === 'loading' ? (
        <p aria-busy="true" className="px-5 py-8 text-center text-sm text-muted sm:px-6">
          {t('chinaPayment.rates.loading')}
        </p>
      ) : status === 'error' ? (
        <div className="grid justify-items-center gap-3 px-5 py-8 sm:px-6">
          <p className="text-center text-sm text-ink">{t('chinaPayment.rates.failed')}</p>
          <button type="button" onClick={onRetry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('actions.retry')}
          </button>
        </div>
      ) : !card ? (
        <p className="px-5 py-8 text-center text-sm text-muted sm:px-6">
          {t('chinaPayment.rates.unavailable')}
        </p>
      ) : (
        <div className="px-5 py-4 sm:px-6">
          {card.active ? null : (
            <p
              role="status"
              className="mb-3 flex items-start gap-2 rounded-md bg-surface-alt p-3 text-sm text-ink"
            >
              <Icon name="info" size={18} className="mt-px shrink-0 text-muted" />
              {t('chinaPayment.rates.paused')}
            </p>
          )}

          <RateTable tiers={card.tiers} />
          <FeeList card={card} />

          <p className="mt-4 text-xs text-muted">{t('chinaPayment.rates.note')}</p>
        </div>
      )}
    </Card>
  );
}

function RateTable({ tiers }: { tiers: readonly RateTier[] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const rm = (cents: number) => fmt.money(cents, 'RM');

  const label = (tier: RateTier) =>
    tier.toRmCents === undefined
      ? t('chinaPayment.rates.andAbove', { from: rm(tier.fromRmCents) })
      : tier.fromRmCents === 0
        ? t('chinaPayment.rates.under', { to: rm(tier.toRmCents) })
        : t('chinaPayment.rates.between', { from: rm(tier.fromRmCents), to: rm(tier.toRmCents) });

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-muted">
          <th scope="col" className="pb-2 font-medium">
            {t('chinaPayment.rates.amountHeader')}
          </th>
          <th scope="col" className="pb-2 text-right font-medium">
            {t('chinaPayment.rates.rateHeader')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {tiers.map((tier) => (
          <tr key={tier.fromRmCents}>
            <th scope="row" className="py-2.5 pr-3 text-left font-normal text-ink tabular-nums">
              {label(tier)}
            </th>
            <td className="py-2.5 text-right font-semibold text-ink tabular-nums">
              {t('chinaPayment.rates.rateValue', { rate: tier.rate })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FeeList({ card }: { card: RateCardData }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { topUp, proxyPay, bankTransferRmbCents } = card.fees;

  const percent = (rule: PercentFeeRule) =>
    t('chinaPayment.rates.percentRule', {
      percent: fmt.number(rule.percent, Number.isInteger(rule.percent) ? 0 : 2),
      flat: fmt.money(rule.flatFeeCents, rule.flatFeeCurrency),
      below: fmt.money(rule.flatBelowRmbCents, 'RMB'),
    });

  const rows = [
    topUp ? { key: 'topUp', name: t('chinaPayment.rates.feeTopUp'), value: percent(topUp) } : null,
    proxyPay
      ? { key: 'proxyPay', name: t('chinaPayment.rates.feeProxyPay'), value: percent(proxyPay) }
      : null,
    bankTransferRmbCents === undefined
      ? null
      : {
          key: 'bank',
          name: t('chinaPayment.rates.feeBankTransfer'),
          value: t('chinaPayment.rates.perTransfer', {
            fee: fmt.money(bankTransferRmbCents, 'RMB'),
          }),
        },
  ].filter((row) => row !== null);

  if (rows.length === 0) return null;

  return (
    <div className="mt-5">
      <h3 className="text-xs font-medium text-muted">{t('chinaPayment.rates.feesTitle')}</h3>
      <dl className="mt-2 divide-y divide-line text-sm">
        {rows.map((row) => (
          <div key={row.key} className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 py-2.5">
            <dt className="text-ink">{row.name}</dt>
            <dd className="font-medium text-ink tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
